import React, { ReactElement, useEffect, useState, useCallback } from "react";
import { AgGrid, Button, Flex } from "@jsq/ds/core";
import { SPACING } from "#utils/spacing";
import type { SavableGridConfiguration } from "../hooks/useGridViewManager";

type SavedViewPanelProps = {
  api: AgGrid.GridApi;
  saveViewLoading: boolean;
  handleSavedViewClicked: (api: AgGrid.GridApi) => Promise<void>;
  savedConfiguration: SavableGridConfiguration | null;
};

const isFilterModelEqual = (
  current: AgGrid.FilterModel | null | undefined,
  saved: AgGrid.FilterModel | undefined,
): boolean => {
  if (!saved) return !current || Object.keys(current).length === 0;
  return JSON.stringify(current ?? {}) === JSON.stringify(saved);
};

export const SavedViewPanel = ({
  api,
  saveViewLoading,
  handleSavedViewClicked,
  savedConfiguration,
}: SavedViewPanelProps): ReactElement => {
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);

  const checkIfStateChanged = useCallback(() => {
    if (api.isDestroyed?.()) return;
    const currentFilterModel = api.getFilterModel();
    const savedFilterModel = savedConfiguration?.filter?.filterModel;
    const hasFilterChanged = !isFilterModelEqual(currentFilterModel, savedFilterModel);
    setIsSaveEnabled(hasFilterChanged);
  }, [api, savedConfiguration]);

  useEffect(() => {
    const updateFilterState = (event?: AgGrid.FilterChangedEvent) => {
      if (event?.source === "api" || event?.source === "columnFilter") {
        checkIfStateChanged();
      }
    };

    const handleGridChange = (event: { source?: string }) => {
      if (event.source !== "api") {
        setIsSaveEnabled(true);
      }
    };

    api.addEventListener("filterChanged", updateFilterState);
    api.addEventListener("columnMoved", handleGridChange);
    api.addEventListener("columnVisible", handleGridChange);
    api.addEventListener("columnResized", handleGridChange);
    api.addEventListener("sortChanged", handleGridChange);

    return () => {
      api.removeEventListener("filterChanged", updateFilterState);
      api.removeEventListener("columnMoved", handleGridChange);
      api.removeEventListener("columnVisible", handleGridChange);
      api.removeEventListener("columnResized", handleGridChange);
      api.removeEventListener("sortChanged", handleGridChange);
    };
  }, [api, checkIfStateChanged]);

  const handleSaveClick = async () => {
    setIsSaveEnabled(false);
    try {
      await handleSavedViewClicked(api);
    } catch {
      setIsSaveEnabled(true);
    }
  };

  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM} style={{ padding: "16px" }}>
      <Button
        variant="primary"
        onClick={() => void handleSaveClick()}
        icon="table-layout"
        iconPosition="left"
        disabled={!isSaveEnabled || saveViewLoading}
        isFullWidth
      >
        Save view
      </Button>
    </Flex>
  );
};
